import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  X, 
  Eye, 
  EyeOff, 
  Play, 
  Pause,
  Maximize2,
  Archive,
  Check,
  Send,
  Upload,
  Trash2,
  Plus,
  Globe,
  DollarSign,
  Users,
  Lock,
  Heart,
  Crown,
  Sparkles,
  BookOpen,
  Swords,
  Vault,
  MapPin,
  TrendingUp,
  Settings,
  ChevronLeft,
  ChevronRight,
  Image,
  Video,
  File
} from 'lucide-react';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Vortex Visual Component (Central Media Player)
const VortexVisual = ({ selectedItem, onApprove, onArchive }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  return (
    <div className="relative w-full h-full bg-black flex items-center justify-center overflow-hidden">
      {/* Vortex Animation Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 animate-spin-slow" style={{
          background: 'conic-gradient(from 0deg, transparent 0deg, #FF0000 10deg, transparent 20deg)',
          animationDuration: '20s'
        }} />
      </div>

      {/* Central Display */}
      <div className="relative z-10 w-full max-w-4xl aspect-video bg-black/80 border border-[#FF0000]/30 flex items-center justify-center">
        {selectedItem ? (
          selectedItem.file_type?.startsWith('video') ? (
            <video 
              src={selectedItem.file_url} 
              className="w-full h-full object-contain"
              controls={isPlaying}
            />
          ) : selectedItem.file_type?.startsWith('image') ? (
            <img 
              src={selectedItem.file_url} 
              alt={selectedItem.filename}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="text-center">
              <File className="w-16 h-16 text-[#FF0000]/50 mx-auto mb-4" />
              <p className="font-mono text-sm text-[#888]">{selectedItem.filename}</p>
            </div>
          )
        ) : (
          <div className="text-center">
            <Eye className="w-20 h-20 text-[#FF0000]/30 mx-auto mb-4" />
            <p className="font-heading text-xl text-[#FF0000]/50 uppercase tracking-widest">
              L'OEIL DU MAÎTRE
            </p>
            <p className="font-mono text-xs text-[#444] mt-2">
              Sélectionnez un élément à visualiser
            </p>
          </div>
        )}
      </div>

      {/* Control Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/90 border-t border-[#FF0000]/20 p-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsPlaying(!isPlaying)}
              className="p-2 border border-[#FF0000]/30 hover:border-[#FF0000] hover:bg-[#FF0000]/10 transition-colors"
            >
              {isPlaying ? <Pause className="w-4 h-4 text-[#FF0000]" /> : <Play className="w-4 h-4 text-[#FF0000]" />}
            </button>
            <button 
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 border border-[#FF0000]/30 hover:border-[#FF0000] hover:bg-[#FF0000]/10 transition-colors"
            >
              <Maximize2 className="w-4 h-4 text-[#FF0000]" />
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={onApprove}
              disabled={!selectedItem}
              className="px-4 py-2 bg-[#FF0000]/20 border border-[#FF0000]/50 text-[#FF0000] font-heading text-xs uppercase tracking-widest hover:bg-[#FF0000]/30 disabled:opacity-30 transition-colors"
            >
              <Check className="w-4 h-4 inline mr-2" />
              DÉPLOYER
            </button>
            <button 
              onClick={onArchive}
              disabled={!selectedItem}
              className="px-4 py-2 border border-white/20 text-white/60 font-heading text-xs uppercase tracking-widest hover:border-white/40 disabled:opacity-30 transition-colors"
            >
              <Archive className="w-4 h-4 inline mr-2" />
              ARCHIVER
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Intimacy Panel Component
const IntimacyPanel = ({ token, activeMode, setActiveMode }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'master', content: userMessage }]);
    setLoading(true);
    try {
      const response = await fetch(`${API}/sanctuary/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, mode: activeMode })
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let msgAdded = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const lines = decoder.decode(value).split('\n');
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.token) {
              fullText += parsed.token;
              if (!msgAdded) {
                setMessages(prev => [...prev, { role: 'selena', content: fullText, mode: 'streaming' }]);
                msgAdded = true;
              } else {
                setMessages(prev => { const u = [...prev]; u[u.length-1] = { role: 'selena', content: fullText, mode: 'streaming' }; return u; });
              }
            }
          } catch(e) {}
        }
      }
    } catch (error) {
      toast.error('Erreur de communication');
    } finally {
      setLoading(false);
    }
  };

  const modes = [
    { id: 'education', label: 'ÉDUCATION', icon: BookOpen, color: '#00FF9D' },
    { id: 'donjon', label: 'DONJON', icon: Crown, color: '#FF0000' },
    { id: 'strategist', label: 'STRATÈGE', icon: Swords, color: '#FFD700' }
  ];

  return (
    <div className="h-full flex flex-col bg-black border-r border-[#FF0000]/20">
      {/* Mode Selector */}
      <div className="p-4 border-b border-[#FF0000]/20">
        <h3 className="font-heading text-xs uppercase tracking-widest text-[#FF0000] mb-3">
          INTIMITÉ SOUVERAINE
        </h3>
        <div className="space-y-2">
          {modes.map(mode => (
            <button
              key={mode.id}
              data-testid={`mode-${mode.id}-btn`}
              onClick={() => setActiveMode(mode.id)}
              className={`w-full p-3 flex items-center gap-3 border transition-colors ${
                activeMode === mode.id 
                  ? 'bg-[#FF0000]/10 border-[#FF0000]/50' 
                  : 'border-white/10 hover:border-[#FF0000]/30'
              }`}
            >
              <mode.icon className="w-4 h-4" style={{ color: mode.color }} />
              <span className="font-heading text-xs uppercase tracking-widest text-white/80">
                {mode.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Heart className="w-10 h-10 text-[#FF0000]/20 mx-auto mb-3" />
            <p className="font-mono text-xs text-[#444]">
              {activeMode === 'education' && "Mode Éducation - Dressage de l'IA"}
              {activeMode === 'donjon' && "Mode Donjon - Sans limite"}
              {activeMode === 'strategist' && "Mode Stratège - Analyse avancée"}
            </p>
          </div>
        )}

        {messages.map((msg, index) => (
          <div 
            key={index}
            className={`p-3 ${
              msg.role === 'master' 
                ? 'bg-[#FF0000]/10 border border-[#FF0000]/30 ml-8' 
                : 'bg-[#00FF9D]/5 border border-[#00FF9D]/20 mr-8'
            }`}
          >
            <span className={`font-mono text-[10px] uppercase ${
              msg.role === 'master' ? 'text-[#FF0000]' : 'text-[#00FF9D]'
            }`}>
              {msg.role === 'master' ? 'MAÎTRE' : 'SELENA'}
            </span>
            <p className="font-mono text-sm text-white/80 mt-1 whitespace-pre-wrap">
              {msg.content}
            </p>
          </div>
        ))}

        {loading && (
          <div className="bg-[#00FF9D]/5 border border-[#00FF9D]/20 p-3 mr-8">
            <span className="font-mono text-[10px] text-[#00FF9D]">SELENA</span>
            <span className="animate-blink font-mono text-[#00FF9D] ml-2">_</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 border-t border-[#FF0000]/20">
        <div className="flex gap-2">
          <input
            data-testid="sanctuary-chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ordonne, Maître..."
            className="flex-1 bg-black border border-[#FF0000]/30 text-white font-mono text-sm px-3 py-2 focus:border-[#FF0000] focus:outline-none placeholder:text-[#444]"
            disabled={loading}
          />
          <button
            data-testid="sanctuary-chat-send-btn"
            type="submit"
            disabled={loading || !input.trim()}
            className="p-2 bg-[#FF0000] text-white disabled:opacity-30"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

// Vault Panel Component
const VaultPanel = ({ token, vaultItems, setVaultItems, onSelectItem }) => {
  const [filter, setFilter] = useState('all');
  const fileInputRef = useRef(null);

  const fetchVault = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sanctuary/vault`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVaultItems(response.data);
    } catch (error) {
      console.error('Failed to fetch vault');
    }
  }, [token, setVaultItems]);

  useEffect(() => {
    fetchVault();
  }, [token, fetchVault]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    // Simulate upload (in real app, would upload to storage)
    for (const file of files) {
      const item = {
        filename: file.name,
        file_type: file.type,
        file_url: URL.createObjectURL(file),
        category: 'uploaded',
        tags: []
      };

      try {
        await axios.post(`${API}/sanctuary/vault`, item, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${file.name} sécurisé dans le coffre`);
      } catch (error) {
        toast.error('Erreur lors de l\'upload');
      }
    }
    fetchVault();
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/sanctuary/vault/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Élément purgé');
      fetchVault();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredItems = vaultItems.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'images') return item.file_type?.startsWith('image');
    if (filter === 'videos') return item.file_type?.startsWith('video');
    return true;
  });

  return (
    <div className="h-full flex flex-col bg-black">
      {/* Header */}
      <div className="p-4 border-b border-[#FF0000]/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#FF0000]" />
            <h3 className="font-heading text-xs uppercase tracking-widest text-[#FF0000]">
              COFFRE-FORT
            </h3>
          </div>
          <span className="font-mono text-[10px] text-[#00FF9D]">AES-256</span>
        </div>

        <div className="flex gap-2 mb-3">
          {['all', 'images', 'videos'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 font-mono text-[10px] uppercase border ${
                filter === f 
                  ? 'border-[#FF0000] text-[#FF0000]' 
                  : 'border-white/20 text-white/50 hover:border-white/40'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full p-2 border border-dashed border-[#FF0000]/30 text-[#FF0000]/60 hover:border-[#FF0000] hover:text-[#FF0000] transition-colors flex items-center justify-center gap-2"
        >
          <Upload className="w-4 h-4" />
          <span className="font-heading text-xs uppercase">Upload Crypté</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleUpload}
          className="hidden"
        />
      </div>

      {/* Items Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-2 gap-2">
          {filteredItems.map((item, index) => (
            <div
              key={item.id || index}
              onClick={() => onSelectItem(item)}
              className="aspect-square bg-[#0A0A0A] border border-white/10 hover:border-[#FF0000]/50 cursor-pointer relative group overflow-hidden"
            >
              {item.file_type?.startsWith('image') ? (
                <img src={item.file_url || item.thumbnail_url} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
              ) : item.file_type?.startsWith('video') ? (
                <div className="w-full h-full flex items-center justify-center bg-[#111]">
                  <Video className="w-8 h-8 text-[#FF0000]/50" />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <File className="w-8 h-8 text-white/30" />
                </div>
              )}
              
              <button
                onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }}
                className="absolute top-1 right-1 p-1 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-3 h-3 text-[#FF0000]" />
              </button>
              
              <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="font-mono text-[8px] text-white/60 truncate">{item.filename}</p>
              </div>
            </div>
          ))}
          
          {filteredItems.length === 0 && (
            <div className="col-span-2 text-center py-8">
              <Lock className="w-8 h-8 text-[#FF0000]/20 mx-auto mb-2" />
              <p className="font-mono text-xs text-[#444]">Coffre vide</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Strategy Panel Component
const StrategyPanel = ({ token }) => {
  const [revenueData, setRevenueData] = useState(null);
  const [hotesses, setHotesses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newHotesse, setNewHotesse] = useState({
    codename: '',
    archetype: '',
    personality_traits: [],
    physical_traits: {},
    deployment_location: '',
    coordinates: { lat: 0, lng: 0 },
    monthly_revenue: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const [revRes, hotRes] = await Promise.all([
        axios.get(`${API}/sanctuary/revenues`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sanctuary/hotesses`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRevenueData(revRes.data);
      setHotesses(hotRes.data);
    } catch (error) {
      console.error('Failed to fetch strategy data');
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [token, fetchData]);

  const createHotesse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/sanctuary/hotesses`, newHotesse, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modèle IA créé');
      setShowForm(false);
      setNewHotesse({
        codename: '',
        archetype: '',
        personality_traits: [],
        physical_traits: {},
        deployment_location: '',
        coordinates: { lat: 0, lng: 0 },
        monthly_revenue: 0
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const deleteHotesse = async (id) => {
    if (!window.confirm('Terminer ce modèle ?')) return;
    try {
      await axios.delete(`${API}/sanctuary/hotesses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modèle terminé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black border border-[#FF0000]/50 p-2">
          <p className="font-mono text-xs text-[#888]">{payload[0].payload.month}</p>
          <p className="font-mono text-sm text-[#FF0000]">
            €{payload[0].value?.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-full flex flex-col bg-black border-l border-[#FF0000]/20 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-[#FF0000]/20">
        <h3 className="font-heading text-xs uppercase tracking-widest text-[#FF0000] mb-1">
          ATELIER & STRATÉGIE
        </h3>
        <p className="font-mono text-[10px] text-[#444]">Gestion des modèles IA</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Revenue Stats */}
        <div className="p-4 border-b border-[#FF0000]/10">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-[#FF0000]/5 border border-[#FF0000]/20">
              <p className="font-mono text-[10px] text-[#888] mb-1">REVENUS SECRETS</p>
              <p className="font-mono text-lg text-[#FF0000]">
                €{(revenueData?.total_monthly || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-[#00FF9D]/5 border border-[#00FF9D]/20">
              <p className="font-mono text-[10px] text-[#888] mb-1">MODÈLES ACTIFS</p>
              <p className="font-mono text-lg text-[#00FF9D]">
                {revenueData?.active_hotesses || 0}
              </p>
            </div>
          </div>

          {/* Mini Chart */}
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData?.cascade_data || []}>
                <defs>
                  <linearGradient id="colorSecretRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF0000" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FF0000" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" hide />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#FF0000" 
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorSecretRev)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Character Generator */}
        <div className="p-4 border-b border-[#FF0000]/10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FFD700]" />
              <span className="font-heading text-xs uppercase tracking-widest text-white/80">
                GÉNÉRATEUR
              </span>
            </div>
            <button
              data-testid="add-hotesse-btn"
              onClick={() => setShowForm(!showForm)}
              className="p-1 border border-[#FF0000]/30 hover:border-[#FF0000]"
            >
              {showForm ? <X className="w-4 h-4 text-[#FF0000]" /> : <Plus className="w-4 h-4 text-[#FF0000]" />}
            </button>
          </div>

          {showForm && (
            <form onSubmit={createHotesse} className="space-y-2 mb-4">
              <input
                data-testid="hotesse-codename-input"
                type="text"
                placeholder="Codename"
                value={newHotesse.codename}
                onChange={(e) => setNewHotesse({...newHotesse, codename: e.target.value})}
                className="w-full bg-black border border-[#FF0000]/30 text-white font-mono text-xs px-2 py-2 focus:border-[#FF0000] focus:outline-none"
                required
              />
              <select
                value={newHotesse.archetype}
                onChange={(e) => setNewHotesse({...newHotesse, archetype: e.target.value})}
                className="w-full bg-black border border-[#FF0000]/30 text-white font-mono text-xs px-2 py-2 focus:border-[#FF0000] focus:outline-none"
              >
                <option value="">Archétype...</option>
                <option value="seductrice">Séductrice</option>
                <option value="innocente">Innocente</option>
                <option value="dominatrice">Dominatrice</option>
                <option value="soumise">Soumise</option>
                <option value="mystique">Mystique</option>
              </select>
              <input
                type="text"
                placeholder="Localisation"
                value={newHotesse.deployment_location}
                onChange={(e) => setNewHotesse({...newHotesse, deployment_location: e.target.value})}
                className="w-full bg-black border border-[#FF0000]/30 text-white font-mono text-xs px-2 py-2 focus:border-[#FF0000] focus:outline-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder="Latitude"
                  value={newHotesse.coordinates.lat}
                  onChange={(e) => setNewHotesse({
                    ...newHotesse, 
                    coordinates: {...newHotesse.coordinates, lat: parseFloat(e.target.value) || 0}
                  })}
                  className="bg-black border border-[#FF0000]/30 text-white font-mono text-xs px-2 py-2 focus:border-[#FF0000] focus:outline-none"
                  step="0.001"
                />
                <input
                  type="number"
                  placeholder="Longitude"
                  value={newHotesse.coordinates.lng}
                  onChange={(e) => setNewHotesse({
                    ...newHotesse, 
                    coordinates: {...newHotesse.coordinates, lng: parseFloat(e.target.value) || 0}
                  })}
                  className="bg-black border border-[#FF0000]/30 text-white font-mono text-xs px-2 py-2 focus:border-[#FF0000] focus:outline-none"
                  step="0.001"
                />
              </div>
              <input
                type="number"
                placeholder="Revenue mensuel €"
                value={newHotesse.monthly_revenue}
                onChange={(e) => setNewHotesse({...newHotesse, monthly_revenue: parseFloat(e.target.value) || 0})}
                className="w-full bg-black border border-[#FF0000]/30 text-white font-mono text-xs px-2 py-2 focus:border-[#FF0000] focus:outline-none"
              />
              <button
                data-testid="submit-hotesse-btn"
                type="submit"
                className="w-full py-2 bg-[#FF0000] text-white font-heading text-xs uppercase tracking-widest"
              >
                CRÉER MODÈLE
              </button>
            </form>
          )}

          {/* Hotesses List */}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {hotesses.map((h, i) => (
              <div 
                key={h.id || i}
                className="p-2 bg-[#0A0A0A] border border-white/10 hover:border-[#FF0000]/30 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading text-xs text-white uppercase">{h.codename}</p>
                    <p className="font-mono text-[10px] text-[#888]">
                      {h.archetype || 'Non défini'} • {h.deployment_location || 'N/A'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[#00FF9D]">
                      €{(h.monthly_revenue || 0).toLocaleString()}
                    </span>
                    <button onClick={() => deleteHotesse(h.id)}>
                      <Trash2 className="w-3 h-3 text-[#FF0000]/50 hover:text-[#FF0000]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {hotesses.length === 0 && (
              <p className="font-mono text-xs text-[#444] text-center py-4">Aucun modèle</p>
            )}
          </div>
        </div>

        {/* Deployment Map Placeholder */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-[#00BFFF]" />
            <span className="font-heading text-xs uppercase tracking-widest text-white/80">
              DÉPLOIEMENT GLOBAL
            </span>
          </div>
          <div className="h-32 bg-[#0A0A0A] border border-[#FF0000]/10 flex items-center justify-center relative overflow-hidden">
            {/* Simple world map representation */}
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 50\'%3E%3Cpath fill=\'none\' stroke=\'%23FF0000\' stroke-width=\'0.5\' d=\'M10,25 Q25,10 40,25 T70,25 T90,25\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'repeat'
            }} />
            <div className="text-center z-10">
              <MapPin className="w-6 h-6 text-[#FF0000] mx-auto mb-1" />
              <p className="font-mono text-xs text-[#888]">
                {hotesses.length} déployés
              </p>
            </div>
            {/* Animated dots for deployed locations */}
            {hotesses.slice(0, 5).map((h, i) => (
              <div 
                key={i}
                className="absolute w-2 h-2 bg-[#FF0000] rounded-full animate-pulse"
                style={{
                  left: `${20 + (i * 15)}%`,
                  top: `${30 + (i % 3) * 15}%`,
                  animationDelay: `${i * 0.3}s`
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Sanctuary Module (Full-screen Modal)
export const SanctuaryModule = ({ isOpen, onClose, token }) => {
  const [activeMode, setActiveMode] = useState('education');
  const [selectedVaultItem, setSelectedVaultItem] = useState(null);
  const [vaultItems, setVaultItems] = useState([]);
  const [activePanel, setActivePanel] = useState('intimacy'); // 'intimacy', 'vault', 'strategy'

  if (!isOpen) return null;

  const handleApprove = () => {
    if (selectedVaultItem) {
      toast.success('Élément approuvé pour déploiement');
    }
  };

  const handleArchive = () => {
    if (selectedVaultItem) {
      toast.success('Élément archivé dans le coffre');
      setSelectedVaultItem(null);
    }
  };

  return (
    <div 
      data-testid="sanctuary-modal"
      className="fixed inset-0 z-[9999] bg-black"
      style={{ animation: 'fadeIn 0.5s ease-out' }}
    >
      {/* Scanlines Effect */}
      <div className="pointer-events-none absolute inset-0 opacity-10 z-[10000]" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,0,0,0.03) 2px, rgba(255,0,0,0.03) 4px)'
      }} />

      {/* Header Bar */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-black border-b border-[#FF0000]/30 flex items-center justify-between px-4 z-[10001]">
        <div className="flex items-center gap-3">
          <Crown className="w-5 h-5 text-[#FF0000]" />
          <h1 className="font-heading text-lg uppercase tracking-[0.3em] text-[#FF0000]">
            JARDIN SECRET SANCTUARY
          </h1>
          <span className="font-mono text-[10px] text-[#444] ml-4">
            DEEP STEALTH MODE
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-mono text-[10px] text-[#00FF9D] flex items-center gap-2">
            <Lock className="w-3 h-3" />
            SOVEREIGN ACCESS
          </span>
          <button
            data-testid="sanctuary-close-btn"
            onClick={onClose}
            className="p-2 border border-[#FF0000]/30 hover:border-[#FF0000] hover:bg-[#FF0000]/10 transition-colors"
          >
            <X className="w-4 h-4 text-[#FF0000]" />
          </button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="absolute top-12 bottom-0 left-0 right-0 flex">
        {/* Left Panel Navigation */}
        <div className="w-12 bg-black border-r border-[#FF0000]/20 flex flex-col items-center py-4 gap-2">
          <button
            onClick={() => setActivePanel('intimacy')}
            className={`p-2 border transition-colors ${
              activePanel === 'intimacy' 
                ? 'border-[#FF0000] bg-[#FF0000]/10' 
                : 'border-white/10 hover:border-[#FF0000]/50'
            }`}
            title="Intimité"
          >
            <Heart className="w-4 h-4 text-[#FF0000]" />
          </button>
          <button
            onClick={() => setActivePanel('vault')}
            className={`p-2 border transition-colors ${
              activePanel === 'vault' 
                ? 'border-[#FF0000] bg-[#FF0000]/10' 
                : 'border-white/10 hover:border-[#FF0000]/50'
            }`}
            title="Coffre-Fort"
          >
            <Lock className="w-4 h-4 text-[#FFD700]" />
          </button>
          <button
            onClick={() => setActivePanel('strategy')}
            className={`p-2 border transition-colors ${
              activePanel === 'strategy' 
                ? 'border-[#FF0000] bg-[#FF0000]/10' 
                : 'border-white/10 hover:border-[#FF0000]/50'
            }`}
            title="Stratégie"
          >
            <TrendingUp className="w-4 h-4 text-[#00FF9D]" />
          </button>
        </div>

        {/* Left Panel Content */}
        <div className="w-80 shrink-0">
          {activePanel === 'intimacy' && (
            <IntimacyPanel 
              token={token} 
              activeMode={activeMode} 
              setActiveMode={setActiveMode} 
            />
          )}
          {activePanel === 'vault' && (
            <VaultPanel 
              token={token}
              vaultItems={vaultItems}
              setVaultItems={setVaultItems}
              onSelectItem={setSelectedVaultItem}
            />
          )}
          {activePanel === 'strategy' && (
            <StrategyPanel token={token} />
          )}
        </div>

        {/* Central Vortex Visual */}
        <div className="flex-1 bg-black">
          <VortexVisual 
            selectedItem={selectedVaultItem}
            onApprove={handleApprove}
            onArchive={handleArchive}
          />
        </div>

        {/* Right Panel - Always Strategy Overview */}
        <div className="w-72 shrink-0">
          <StrategyPanel token={token} />
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-spin-slow {
          animation: spin 20s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SanctuaryModule;

